import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Typography, Button, Card, CardContent, Grid, Chip, LinearProgress,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Stack, CircularProgress, Alert, useTheme, useMediaQuery,
  Divider, Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  IconButton, Tabs, Tab,
} from '@mui/material';
import {
  ArrowBack, Add, Edit, Agriculture, CalendarToday, AccountBalance,
  ReceiptLong, RequestPage, Payments, HandshakeOutlined, EditNote,
  Grass, Delete, Link as LinkIcon, Pause, PlayArrow, History,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  projectService, farmOperationService, expenseService,
  paymentRequestService, paymentService, contractService, intercropLinkService,
  activityFeedService,
} from '@smartgrow/shared';

const activityTypeLabel = (t: string) => t.replace(/_/g, ' ');

const fmt = (n: number) => `₦${n.toLocaleString('en-NG')}`;
const fmtDate = (s?: string) => s ? new Date(s).toLocaleDateString('en-NG') : '—';

const statusColor = (s: string): 'success' | 'primary' | 'warning' | 'default' | 'error' => {
  switch (s) {
    case 'IN_PROGRESS': case 'ACTIVE': return 'success';
    case 'COMPLETED': case 'APPROVED': return 'primary';
    case 'PLANNING': case 'PENDING': case 'PAUSED': return 'warning';
    case 'CANCELLED': case 'ABANDONED': case 'REJECTED': return 'error';
    default: return 'default';
  }
};

const opTypeLabel = (t: string) => t.replace(/_/g, ' ');

export const ProjectDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const projectId = id!;
  const qc = useQueryClient();

  const userData = localStorage.getItem('user_data');
  const currentUser = userData ? JSON.parse(userData) : null;
  const canViewActivity = currentUser?.role === 'OWNER' || currentUser?.role === 'ADMIN';

  const [tab, setTab] = useState(0);
  const [editBudgetOpen, setEditBudgetOpen] = useState(false);
  const [budgetValue, setBudgetValue] = useState('');
  const [intercropDialogOpen, setIntercropDialogOpen] = useState(false);
  const [intercropForm, setIntercropForm] = useState({ otherProjectId: '', sharedAcres: '', note: '' });
  const [intercropError, setIntercropError] = useState('');

  const { data: project, isLoading: projLoading, isError: projError } = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => projectService.getProjectById(projectId),
    enabled: !!projectId,
  });

  const { data: opsData } = useQuery({
    queryKey: ['farm-ops-project', projectId],
    queryFn: () => farmOperationService.getByProject(projectId),
    enabled: !!projectId,
  });

  const { data: expensesData } = useQuery({
    queryKey: ['expenses-project', projectId],
    queryFn: () => expenseService.getByProject(projectId, 0, 20),
    enabled: !!projectId,
  });

  const { data: paymentRequestsData } = useQuery({
    queryKey: ['payment-requests-project', projectId],
    queryFn: () => paymentRequestService.getByProject(projectId, { page: 0, size: 20 }),
    enabled: !!projectId,
  });

  const { data: paymentsData } = useQuery({
    queryKey: ['payments-project', projectId],
    queryFn: () => paymentService.getByProject(projectId, { page: 0, size: 20 }),
    enabled: !!projectId,
  });

  const { data: contractsData } = useQuery({
    queryKey: ['contracts-project', projectId],
    queryFn: () => contractService.getByProject(projectId, 0, 20),
    enabled: !!projectId,
  });

  const { data: intercropLinks = [] } = useQuery({
    queryKey: ['intercrop-links-project', projectId],
    queryFn: () => intercropLinkService.getByProject(projectId),
    enabled: !!projectId,
  });

  const { data: activityFeed = [] } = useQuery({
    queryKey: ['activity-feed-project', projectId],
    queryFn: () => activityFeedService.getByProject(projectId, 20),
    enabled: !!projectId && canViewActivity,
  });

  const { data: allProjectsData } = useQuery({
    queryKey: ['projects-all'],
    queryFn: () => projectService.getProjects({ size: 100 }),
    enabled: intercropDialogOpen,
  });

  const createIntercropMutation = useMutation({
    mutationFn: () => intercropLinkService.create({
      projectAId: projectId,
      projectBId: intercropForm.otherProjectId,
      sharedAcres: intercropForm.sharedAcres ? Number(intercropForm.sharedAcres) : undefined,
      note: intercropForm.note || undefined,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['intercrop-links-project', projectId] });
      setIntercropDialogOpen(false);
      setIntercropForm({ otherProjectId: '', sharedAcres: '', note: '' });
      setIntercropError('');
    },
    onError: () => setIntercropError('Failed to link project. Please try again.'),
  });

  const deleteIntercropMutation = useMutation({
    mutationFn: (linkId: string) => intercropLinkService.delete(linkId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['intercrop-links-project', projectId] }),
    onError: () => setIntercropError('Failed to remove intercrop link. Please try again.'),
  });

  const updateBudgetMutation = useMutation({
    mutationFn: (budget: number) =>
      projectService.updateProject(projectId, {
        ...project,
        budget,
        projectName: project?.projectName || project?.name,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['project', projectId] });
      setEditBudgetOpen(false);
    },
  });

  const pauseMutation = useMutation({
    mutationFn: () => projectService.pauseProject(projectId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['project', projectId] }),
  });

  const resumeMutation = useMutation({
    mutationFn: () => projectService.resumeProject(projectId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['project', projectId] }),
  });

  const ops = opsData?.content ?? [];
  const expenses = expensesData?.content ?? [];
  const paymentRequests = paymentRequestsData?.content ?? [];
  const payments = paymentsData?.content ?? [];
  const contracts = contractsData?.content ?? [];

  if (projLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', pt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (projError || !project) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Failed to load project.</Alert>
        <Button startIcon={<ArrowBack />} onClick={() => navigate(-1)} sx={{ mt: 2 }}>
          Back to Projects
        </Button>
      </Box>
    );
  }

  const totalSpent = project.totalSpent ?? 0; // disbursed + pending approved (already summed by backend)
  const pending = project.pendingApprovedExpenses ?? 0; // subset of totalSpent: approved but unpaid
  const disbursed = totalSpent - pending; // actually paid out
  const budget = project.budget ?? 0;
  const budgetPct = budget > 0 ? Math.min((totalSpent / budget) * 100, 100) : 0;
  const disbursedPct = budget > 0 ? Math.min((disbursed / budget) * 100, 100) : 0;
  const budgetColor = budgetPct >= 90 ? 'error' : budgetPct >= 70 ? 'warning' : 'success';

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      {/* Header */}
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <Button startIcon={<ArrowBack />} onClick={() => navigate(-1)} size="small" variant="outlined">
          {isMobile ? '' : 'Back'}
        </Button>
        <Box sx={{ flex: 1 }}>
          <Typography variant={isMobile ? 'h6' : 'h5'} fontWeight="bold">
            {project.projectName || project.name}
          </Typography>
          <Typography variant="caption" color="text.secondary">{project.projectCode}</Typography>
        </Box>
        <Chip label={project.status?.replace(/_/g, ' ')} color={statusColor(project.status)} size="small" />
        {project.status === 'ACTIVE' && (
          <Button startIcon={<Pause />} variant="outlined" color="warning" size="small"
            disabled={pauseMutation.isPending}
            onClick={() => pauseMutation.mutate()}>
            {isMobile ? '' : 'Pause'}
          </Button>
        )}
        {project.status === 'PAUSED' && (
          <Button startIcon={<PlayArrow />} variant="outlined" color="success" size="small"
            disabled={resumeMutation.isPending}
            onClick={() => resumeMutation.mutate()}>
            {isMobile ? '' : 'Resume'}
          </Button>
        )}
        <Button startIcon={<Edit />} variant="outlined" size="small"
          onClick={() => navigate(`/dashboard/projects/${projectId}/edit`)}>
          {isMobile ? '' : 'Edit'}
        </Button>
      </Stack>

      <Paper sx={{ mb: 2 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto">
          <Tab label="Overview" />
          <Tab label="Financials" />
          <Tab label="Contracts" />
          {canViewActivity && <Tab label="Activity" />}
        </Tabs>
      </Paper>

      {tab === 0 && (
      <Grid container spacing={2}>
        {/* Info card */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>Project Info</Typography>
              <Stack spacing={1.5}>
                {project.description && <Typography variant="body2">{project.description}</Typography>}
                <Stack direction="row" spacing={1} alignItems="center">
                  <CalendarToday fontSize="small" color="action" />
                  <Typography variant="body2">{fmtDate(project.startDate)} — {fmtDate(project.endDate)}</Typography>
                </Stack>
                {project.location && (
                  <Typography variant="body2" color="text.secondary">📍 {project.location}</Typography>
                )}
                <Typography variant="body2">
                  Manager: <strong>{project.managerName || project.supervisorName || '—'}</strong>
                </Typography>
                {project.notes && (
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    {project.notes}
                  </Typography>
                )}
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Budget card */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <AccountBalance fontSize="small" color="action" />
                  <Typography variant="subtitle2" color="text.secondary">Budget Overview</Typography>
                </Stack>
                <IconButton size="small" onClick={() => { setBudgetValue(String(budget)); setEditBudgetOpen(true); }}>
                  <EditNote fontSize="small" />
                </IconButton>
              </Stack>
              <Stack spacing={2}>
                <Box>
                  <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
                    <Typography variant="caption">Total Committed</Typography>
                    <Typography variant="caption" color={`${budgetColor}.main`}>{budgetPct.toFixed(0)}%</Typography>
                  </Stack>
                  <LinearProgress variant="determinate" value={budgetPct} color={budgetColor} sx={{ height: 8, borderRadius: 4 }} />
                </Box>
                {pending > 0 && (
                  <Box>
                    <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
                      <Typography variant="caption" color="text.secondary">Disbursed</Typography>
                      <Typography variant="caption" color="text.secondary">{disbursedPct.toFixed(0)}%</Typography>
                    </Stack>
                    <LinearProgress variant="determinate" value={disbursedPct} color="success" sx={{ height: 4, borderRadius: 4 }} />
                  </Box>
                )}
                <Divider />
                <Stack direction="row" justifyContent="space-between" flexWrap="wrap" gap={1}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Budget</Typography>
                    <Typography variant="h6" fontWeight="bold">{fmt(budget)}</Typography>
                  </Box>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="caption" color="text.secondary">Spent</Typography>
                    <Typography variant="h6" color={`${budgetColor}.main`} fontWeight="bold">{fmt(totalSpent)}</Typography>
                  </Box>
                  {pending > 0 && (
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="caption" color="text.secondary">Pending Payout</Typography>
                      <Typography variant="h6" color="warning.main" fontWeight="bold">{fmt(pending)}</Typography>
                    </Box>
                  )}
                  <Box sx={{ textAlign: 'right' }}>
                    <Typography variant="caption" color="text.secondary">Remaining</Typography>
                    <Typography variant="h6" fontWeight="bold">{fmt(project.remainingBudget ?? (budget - totalSpent))}</Typography>
                  </Box>
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Farm Operations */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Agriculture fontSize="small" />
                  <Typography variant="subtitle1" fontWeight="bold">Farm Operations ({ops.length})</Typography>
                </Stack>
                <Button startIcon={<Add />} variant="contained" size="small" onClick={() => navigate('/dashboard/farm/new')}>
                  Add Operation
                </Button>
              </Stack>
              {ops.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Agriculture sx={{ fontSize: 48, color: 'text.disabled' }} />
                  <Typography color="text.secondary" sx={{ mt: 1 }}>No farm operations yet</Typography>
                </Box>
              ) : isMobile ? (
                <Stack spacing={1.5}>
                  {ops.map((op) => (
                    <Card key={op.id} variant="outlined" sx={{ cursor: 'pointer' }} onClick={() => navigate(`/dashboard/farm/${op.id}`)}>
                      <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="body2" fontWeight="bold">{op.name}</Typography>
                            <Typography variant="caption" color="text.secondary">{opTypeLabel(op.operationType)}</Typography>
                          </Box>
                          <Chip label={op.status} color={statusColor(op.status)} size="small" />
                        </Stack>
                        <Stack direction="row" justifyContent="space-between" sx={{ mt: 1 }}>
                          <Typography variant="caption">Tasks: {op.completedTasks}/{op.totalTasks}</Typography>
                          {op.totalActualCost && <Typography variant="caption" color="text.secondary">Actual: {fmt(op.totalActualCost)}</Typography>}
                        </Stack>
                        {op.totalTasks > 0 && (
                          <LinearProgress variant="determinate" value={(op.completedTasks / op.totalTasks) * 100} sx={{ mt: 0.5, height: 4, borderRadius: 2 }} />
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </Stack>
              ) : (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Name</TableCell>
                        <TableCell>Type</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Season</TableCell>
                        <TableCell>Tasks</TableCell>
                        <TableCell align="right">Actual Cost</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {ops.map((op) => (
                        <TableRow key={op.id} hover sx={{ cursor: 'pointer' }} onClick={() => navigate(`/dashboard/farm/${op.id}`)}>
                          <TableCell><Typography variant="body2" fontWeight="bold">{op.name}</Typography></TableCell>
                          <TableCell><Typography variant="body2" color="text.secondary">{opTypeLabel(op.operationType)}</Typography></TableCell>
                          <TableCell><Chip label={op.status} color={statusColor(op.status)} size="small" /></TableCell>
                          <TableCell>{op.season ?? '—'}</TableCell>
                          <TableCell>
                            <Stack direction="row" spacing={1} alignItems="center">
                              <Typography variant="caption">{op.completedTasks}/{op.totalTasks}</Typography>
                              {op.totalTasks > 0 && <LinearProgress variant="determinate" value={(op.completedTasks / op.totalTasks) * 100} sx={{ width: 60, height: 4, borderRadius: 2 }} />}
                            </Stack>
                          </TableCell>
                          <TableCell align="right">{op.totalActualCost ? fmt(op.totalActualCost) : '—'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Intercropping */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Grass fontSize="small" />
                  <Typography variant="subtitle1" fontWeight="bold">Intercropping ({intercropLinks.length})</Typography>
                </Stack>
                <Button startIcon={<LinkIcon />} variant="outlined" size="small" onClick={() => setIntercropDialogOpen(true)}>
                  Link Project
                </Button>
              </Stack>
              {intercropError && !intercropDialogOpen && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setIntercropError('')}>{intercropError}</Alert>
              )}
              {intercropLinks.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No intercrop links. Link this project with another that shares physical land (e.g. a groundnut crop planted within a cashew field).
                </Typography>
              ) : (
                <Stack spacing={1}>
                  {intercropLinks.map((link) => {
                    const otherId = link.projectAId === projectId ? link.projectBId : link.projectAId;
                    const otherName = link.projectAId === projectId ? link.projectBName : link.projectAName;
                    return (
                      <Box key={link.id} sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box sx={{ cursor: 'pointer' }} onClick={() => navigate(`/dashboard/projects/${otherId}`)}>
                          <Typography variant="body2" fontWeight="bold" sx={{ '&:hover': { textDecoration: 'underline' } }}>{otherName}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {link.hostOperationName && `Host: ${link.hostOperationName} (${link.hostOperationAcres ?? '—'} acres)`}
                            {link.intercropOperationName && ` · Intercrop: ${link.intercropOperationName} (${link.intercropOperationAcres ?? '—'} acres)`}
                            {link.sharedAcres != null && ` · Shared: ${link.sharedAcres} acres`}
                            {link.note && ` · ${link.note}`}
                          </Typography>
                        </Box>
                        <IconButton size="small" onClick={() => deleteIntercropMutation.mutate(link.id)} disabled={deleteIntercropMutation.isPending}>
                          <Delete fontSize="small" />
                        </IconButton>
                      </Box>
                    );
                  })}
                </Stack>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      )}

      {tab === 1 && (
      <Grid container spacing={2}>
        {/* Payments */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Payments fontSize="small" />
                  <Typography variant="subtitle1" fontWeight="bold">Payments ({paymentsData?.totalElements ?? 0})</Typography>
                </Stack>
                <Button size="small" onClick={() => navigate(`/dashboard/payments?projectId=${projectId}&projectName=${encodeURIComponent(project.projectName || project.name)}`)}>
                  View All
                </Button>
              </Stack>
              {payments.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 3 }}>
                  <Payments sx={{ fontSize: 40, color: 'text.disabled' }} />
                  <Typography color="text.secondary" variant="body2" sx={{ mt: 1 }}>No payments</Typography>
                </Box>
              ) : (
                <Stack spacing={1}>
                  {payments.map((p) => (
                    <Box key={p.id} sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
                      onClick={() => navigate(`/dashboard/payments/${p.id}`)}>
                      <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                        <Box sx={{ flex: 1, mr: 1 }}>
                          <Typography variant="body2" fontWeight="bold" noWrap>
                            {p.paymentType?.replace(/_/g, ' ')} {p.contractorName ? `— ${p.contractorName}` : ''}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {p.referenceNumber ?? p.paymentMethod} · {fmtDate(p.paymentDate)}
                          </Typography>
                        </Box>
                        <Stack alignItems="flex-end" spacing={0.5}>
                          <Chip label={p.status} color={statusColor(p.status)} size="small" />
                          <Typography variant="body2" fontWeight="bold" color="error.main">{fmt(p.amount)}</Typography>
                        </Stack>
                      </Stack>
                      {p.notes && <Typography variant="caption" color="text.secondary">{p.notes}</Typography>}
                    </Box>
                  ))}
                  {(paymentsData?.totalElements ?? 0) > 20 && (
                    <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center', pt: 1 }}>
                      Showing first 20 of {paymentsData?.totalElements}
                    </Typography>
                  )}
                </Stack>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Expense Requests */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <ReceiptLong fontSize="small" />
                  <Typography variant="subtitle1" fontWeight="bold">Expense Requests ({expensesData?.totalElements ?? 0})</Typography>
                </Stack>
                <Button size="small" onClick={() => navigate(`/dashboard/expenses?projectId=${projectId}&projectName=${encodeURIComponent(project.projectName || project.name)}`)}>
                  View All
                </Button>
              </Stack>
              {expenses.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 3 }}>
                  <ReceiptLong sx={{ fontSize: 40, color: 'text.disabled' }} />
                  <Typography color="text.secondary" variant="body2" sx={{ mt: 1 }}>No expense requests</Typography>
                </Box>
              ) : (
                <Stack spacing={1}>
                  {expenses.map((exp) => (
                    <Box key={exp.id} sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
                      onClick={() => navigate(`/dashboard/expenses/${exp.id}`)}>
                      <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                        <Box sx={{ flex: 1, mr: 1 }}>
                          <Typography variant="body2" fontWeight="bold" noWrap>{exp.description}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {exp.requestNumber} · {fmtDate(exp.requestDate)}
                          </Typography>
                        </Box>
                        <Stack alignItems="flex-end" spacing={0.5}>
                          <Chip label={exp.status} color={statusColor(exp.status)} size="small" />
                          <Typography variant="body2" fontWeight="bold">{fmt(exp.amount)}</Typography>
                        </Stack>
                      </Stack>
                      {exp.categoryName && <Typography variant="caption" color="text.secondary">{exp.categoryName}</Typography>}
                    </Box>
                  ))}
                  {(expensesData?.totalElements ?? 0) > 20 && (
                    <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center', pt: 1 }}>
                      Showing first 20 of {expensesData?.totalElements}
                    </Typography>
                  )}
                </Stack>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Payment Requests */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <RequestPage fontSize="small" />
                  <Typography variant="subtitle1" fontWeight="bold">Payment Requests ({paymentRequestsData?.totalElements ?? 0})</Typography>
                </Stack>
                <Button size="small" onClick={() => navigate(`/dashboard/payments/requests?projectId=${projectId}&projectName=${encodeURIComponent(project.projectName || project.name)}`)}>
                  View All
                </Button>
              </Stack>
              {paymentRequests.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 3 }}>
                  <RequestPage sx={{ fontSize: 40, color: 'text.disabled' }} />
                  <Typography color="text.secondary" variant="body2" sx={{ mt: 1 }}>No payment requests</Typography>
                </Box>
              ) : (
                <Stack spacing={1}>
                  {paymentRequests.map((pr) => (
                    <Box key={pr.id} sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                      <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                        <Box sx={{ flex: 1, mr: 1 }}>
                          <Typography variant="body2" fontWeight="bold" noWrap>{pr.description}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {pr.requestNumber} · {pr.requestType?.replace(/_/g, ' ')} · {fmtDate(pr.requestDate)}
                          </Typography>
                        </Box>
                        <Stack alignItems="flex-end" spacing={0.5}>
                          <Chip label={pr.status} color={statusColor(pr.status)} size="small" />
                          <Typography variant="body2" fontWeight="bold">{fmt(pr.amount)}</Typography>
                        </Stack>
                      </Stack>
                      {pr.requestedByName && <Typography variant="caption" color="text.secondary">By {pr.requestedByName}</Typography>}
                    </Box>
                  ))}
                  {(paymentRequestsData?.totalElements ?? 0) > 20 && (
                    <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center', pt: 1 }}>
                      Showing first 20 of {paymentRequestsData?.totalElements}
                    </Typography>
                  )}
                </Stack>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      )}

      {tab === 2 && (
      <Grid container spacing={2}>
        {/* Contracts */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <HandshakeOutlined fontSize="small" />
                  <Typography variant="subtitle1" fontWeight="bold">Contracts ({contractsData?.totalElements ?? 0})</Typography>
                </Stack>
                <Button size="small" onClick={() => navigate(`/dashboard/contracts?projectId=${projectId}&projectName=${encodeURIComponent(project.projectName || project.name)}`)}>
                  View All
                </Button>
              </Stack>
              {contracts.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 3 }}>
                  <HandshakeOutlined sx={{ fontSize: 40, color: 'text.disabled' }} />
                  <Typography color="text.secondary" variant="body2" sx={{ mt: 1 }}>No contracts</Typography>
                </Box>
              ) : (
                <Stack spacing={1}>
                  {contracts.map((c) => (
                    <Box key={c.id} sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
                      onClick={() => navigate(`/dashboard/contracts/${c.id}`)}>
                      <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                        <Box sx={{ flex: 1, mr: 1 }}>
                          <Typography variant="body2" fontWeight="bold" noWrap>{c.contractorCompanyName}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {c.contractNumber} · {fmtDate(c.startDate)}
                          </Typography>
                        </Box>
                        <Stack alignItems="flex-end" spacing={0.5}>
                          <Chip label={c.status} color={statusColor(c.status)} size="small" />
                          <Typography variant="body2" fontWeight="bold">{fmt(c.contractValue ?? 0)}</Typography>
                        </Stack>
                      </Stack>
                      {c.description && <Typography variant="caption" color="text.secondary" noWrap>{c.description}</Typography>}
                    </Box>
                  ))}
                  {(contractsData?.totalElements ?? 0) > 20 && (
                    <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center', pt: 1 }}>
                      Showing first 20 of {contractsData?.totalElements}
                    </Typography>
                  )}
                </Stack>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      )}

      {tab === 3 && canViewActivity && (
      <Grid container spacing={2}>
        {/* Activity / Audit Trail */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                <History fontSize="small" />
                <Typography variant="subtitle1" fontWeight="bold">Recent Activity</Typography>
              </Stack>
              {activityFeed.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 3 }}>
                  <History sx={{ fontSize: 40, color: 'text.disabled' }} />
                  <Typography color="text.secondary" variant="body2" sx={{ mt: 1 }}>No activity yet</Typography>
                </Box>
              ) : (
                <Stack spacing={1}>
                  {activityFeed.map((a) => (
                    <Box key={`${a.type}-${a.id}`} sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                      <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                        <Box sx={{ flex: 1, mr: 1 }}>
                          <Typography variant="body2" fontWeight="bold" noWrap>{a.title}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {activityTypeLabel(a.type)}{a.actorName ? ` · ${a.actorName}` : ''} · {fmtDate(a.occurredAt)}
                          </Typography>
                          {a.description && (
                            <Typography variant="caption" color="text.secondary" display="block" noWrap>
                              {a.description}
                            </Typography>
                          )}
                        </Box>
                        {a.amount != null && (
                          <Typography variant="body2" fontWeight="bold">{fmt(a.amount)}</Typography>
                        )}
                      </Stack>
                    </Box>
                  ))}
                </Stack>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      )}

      {/* Budget Edit Dialog */}
      <Dialog open={editBudgetOpen} onClose={() => setEditBudgetOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Edit Budget</DialogTitle>
        <DialogContent>
          <TextField
            label="Budget (₦)"
            type="number"
            fullWidth
            value={budgetValue}
            onChange={(e) => setBudgetValue(e.target.value)}
            inputProps={{ min: 0, step: 1000 }}
            sx={{ mt: 1 }}
            autoFocus
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditBudgetOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!budgetValue || Number(budgetValue) <= 0 || updateBudgetMutation.isPending}
            onClick={() => updateBudgetMutation.mutate(Number(budgetValue))}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Intercrop Link Dialog */}
      <Dialog open={intercropDialogOpen} onClose={() => setIntercropDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Link Intercropped Project</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {intercropError && (
              <Alert severity="error" onClose={() => setIntercropError('')}>{intercropError}</Alert>
            )}
            <TextField
              select fullWidth label="Other Project"
              value={intercropForm.otherProjectId}
              onChange={(e) => setIntercropForm((f) => ({ ...f, otherProjectId: e.target.value }))}
              SelectProps={{ native: true }}
            >
              <option value="" />
              {(allProjectsData?.content ?? [])
                .filter((p) => p.id !== projectId)
                .map((p) => (
                  <option key={p.id} value={p.id}>{p.projectName || p.name}</option>
                ))}
            </TextField>
            <TextField
              label="Shared Acres (optional)" type="number" fullWidth
              value={intercropForm.sharedAcres}
              onChange={(e) => setIntercropForm((f) => ({ ...f, sharedAcres: e.target.value }))}
              inputProps={{ min: 0, step: 0.1 }}
              helperText="How much acreage physically overlaps between the two projects"
            />
            <TextField
              label="Note (optional)" fullWidth multiline rows={2}
              value={intercropForm.note}
              onChange={(e) => setIntercropForm((f) => ({ ...f, note: e.target.value }))}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIntercropDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!intercropForm.otherProjectId || createIntercropMutation.isPending}
            onClick={() => createIntercropMutation.mutate()}
          >
            Link
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
